import { UserPlus, Trash2, User, Users, AlertTriangle } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { StepHeader } from '~/components/forms/StepHeader';

/**
 * NewStudentsStep - Add new students to enroll
 *
 * Handles three types of students:
 * - persistedNewStudents: Students created in a previous submission (editable)
 * - unpersistedNewStudents: Brand new students being added (editable)
 * - persistedNewStudentsToRemove: IDs of persisted students to remove
 */
export function NewStudentsStep({ formData, gradeLevels = [], updateFormData }: any) {

  // Current state of persisted students (from answers - may be edited)
  const persistedNewStudents = formData?.persistedNewStudents || [];

  // Brand new students being added
  const unpersistedNewStudents = formData?.unpersistedNewStudents || [];

  // IDs of persisted students marked for removal
  const persistedNewStudentsToRemove = formData?.persistedNewStudentsToRemove || [];

  const generateTempId = () => {
    return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // Add a new unpersisted student
  const addStudent = () => {
    const updated = [
      ...unpersistedNewStudents,
      {
        tempId: generateTempId(),
        firstName: '',
        lastName: '',
        dateOfBirth: null,
        gender: null,
        gradeLevel: null
      }
    ];
    updateFormData('unpersistedNewStudents', updated);
  };

  // Remove an unpersisted student
  const removeUnpersistedStudent = (tempId: string) => {
    const updated = unpersistedNewStudents.filter((s: any) => s.tempId !== tempId);
    updateFormData('unpersistedNewStudents', updated);

    // Also remove medical info for this temp student
    if (formData?.studentMedicalInfo?.[tempId]) {
      const medicalInfo = { ...formData.studentMedicalInfo };
      delete medicalInfo[tempId];
      updateFormData('studentMedicalInfo', medicalInfo);
    }
  };

  // Toggle removal of a persisted student
  const togglePersistedStudentRemoval = (studentEnrollmentId: string) => {
    let updated;
    if (persistedNewStudentsToRemove.includes(studentEnrollmentId)) {
      // Un-mark for removal
      updated = persistedNewStudentsToRemove.filter((id: string) => id !== studentEnrollmentId);
    } else {
      // Mark for removal
      updated = [...persistedNewStudentsToRemove, studentEnrollmentId];
    }
    updateFormData('persistedNewStudentsToRemove', updated);
  };

  // Update a persisted student's info
  const updatePersistedStudent = (studentEnrollmentId: string, field: string, value: any) => {
    const updated = persistedNewStudents.map((student: any) => {
      if (student.studentEnrollmentId === studentEnrollmentId) {
        return { ...student, [field]: value };
      }
      return student;
    });
    updateFormData('persistedNewStudents', updated);
  };

  // Update an unpersisted student's info
  const updateUnpersistedStudent = (tempId: string, field: string, value: any) => {
    const updated = unpersistedNewStudents.map((student: any) => {
      if (student.tempId === tempId) {
        return { ...student, [field]: value };
      }
      return student;
    });
    updateFormData('unpersistedNewStudents', updated);
  };

  const hasPersistedStudents = persistedNewStudents.length > 0;
  const hasUnpersistedStudents = unpersistedNewStudents.length > 0;
  const hasAnyStudents = hasPersistedStudents || hasUnpersistedStudents;

  // Render the student form fields
  const renderStudentFields = (student: any, updateFn: any, _keyField: string, isPersisted = false) => {
    const studentKey = isPersisted ? student.studentEnrollmentId : student.tempId;
    const isMarkedForRemoval = isPersisted && persistedNewStudentsToRemove.includes(studentKey);

    return (
      <CardContent className={`space-y-4 ${isMarkedForRemoval ? 'opacity-50' : ''}`}>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`firstName-${studentKey}`}>
              First Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`firstName-${studentKey}`}
              value={student.firstName || ''}
              onChange={(e) => updateFn(studentKey, 'firstName', e.target.value)}
              placeholder="First name"
              disabled={isMarkedForRemoval}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`lastName-${studentKey}`}>
              Last Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`lastName-${studentKey}`}
              value={student.lastName || ''}
              onChange={(e) => updateFn(studentKey, 'lastName', e.target.value)}
              placeholder="Last name"
              disabled={isMarkedForRemoval}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor={`dob-${studentKey}`}>
              Date of Birth
            </Label>
            <Input
              id={`dob-${studentKey}`}
              type="date"
              value={student.dateOfBirth || ''}
              onChange={(e) => updateFn(studentKey, 'dateOfBirth', e.target.value)}
              disabled={isMarkedForRemoval}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`gender-${studentKey}`}>
              Gender
            </Label>
            <Select
              value={student.gender || ''}
              onValueChange={(value) => updateFn(studentKey, 'gender', value)}
              disabled={isMarkedForRemoval}
            >
              <SelectTrigger id={`gender-${studentKey}`}>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="MALE">Male</SelectItem>
                <SelectItem value="FEMALE">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`grade-${studentKey}`}>
            Grade Level <span className="text-destructive">*</span>
          </Label>
          <Select
            value={student.gradeLevel || ''}
            onValueChange={(value) => updateFn(studentKey, 'gradeLevel', value)}
            disabled={isMarkedForRemoval}
          >
            <SelectTrigger id={`grade-${studentKey}`}>
              <SelectValue placeholder="Select grade level" />
            </SelectTrigger>
            <SelectContent>
              {gradeLevels.map((grade: any) => (
                <SelectItem
                  key={grade.description || grade}
                  value={grade.description || grade}
                >
                  {grade.description || grade}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    );
  };

  return (
    <div className="space-y-6">
      <StepHeader
        icon={UserPlus}
        title="New Students"
        description="Are you enrolling any new students this year? Add their basic information below."
      />

      <div className="space-y-6 max-w-xl mx-auto">
        {/* Already Persisted Students (from previous submission) */}
        {hasPersistedStudents && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>Previously Added Students</span>
            </div>

            {persistedNewStudents.map((student: any, _index: number) => {
              const isMarkedForRemoval = persistedNewStudentsToRemove.includes(student.studentEnrollmentId);

              return (
                <Card key={student.studentEnrollmentId} className={isMarkedForRemoval ? 'border-destructive/50' : ''}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">
                          {student.firstName} {student.lastName}
                        </CardTitle>
                        <CardDescription>
                          Added in a previous submission
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`remove-${student.studentEnrollmentId}`}
                          checked={isMarkedForRemoval}
                          onCheckedChange={() => togglePersistedStudentRemoval(student.studentEnrollmentId)}
                        />
                        <Label
                          htmlFor={`remove-${student.studentEnrollmentId}`}
                          className="text-sm text-muted-foreground cursor-pointer"
                        >
                          Remove
                        </Label>
                      </div>
                    </div>
                    {isMarkedForRemoval && (
                      <div className="mt-2 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive border border-destructive/20">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">
                          This student will be removed from enrollment upon submission.
                        </span>
                      </div>
                    )}
                  </CardHeader>
                  {renderStudentFields(student, updatePersistedStudent, 'studentEnrollmentId', true)}
                </Card>
              );
            })}
          </div>
        )}

        {/* Unpersisted Students (new additions) */}
        {hasUnpersistedStudents && (
          <div className="space-y-4">
            {hasPersistedStudents && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <UserPlus className="h-4 w-4" />
                <span>New Students</span>
              </div>
            )}

            {unpersistedNewStudents.map((student: any, index: number) => (
              <Card key={student.tempId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      New Student {index + 1}
                      {student.firstName && ` - ${student.firstName}`}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUnpersistedStudent(student.tempId)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                {renderStudentFields(student, updateUnpersistedStudent, 'tempId', false)}
              </Card>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!hasAnyStudents && (
          <Card>
            <CardContent className="py-8 text-center">
              <User className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                No new students added yet. Click below if you'd like to enroll a new child.
              </p>
            </CardContent>
          </Card>
        )}

        <Button
          variant="outline"
          onClick={addStudent}
          className="w-full"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add New Student
        </Button>

        {hasAnyStudents && (
          <p className="text-center text-sm text-muted-foreground">
            You'll be able to provide medical information for new students in a later step.
          </p>
        )}
      </div>
    </div>
  );
}

export default NewStudentsStep;
